import { useState } from 'react';
import { useGetProductsQuery } from '@/services/api/product/productApi';
import { useGetCategoriesQuery } from '@/services/api/category/categoryApi';

// Category names mapping - these should match the actual category names in your database
const CATEGORY_NAMES = {
  FOOD: ['Food', 'Soups', 'Rice', 'Pasta', 'Protein', 'Native Dish', 'Pepper Soup'],
  PLATTER: ['Platter'],
  NON_EDITABLES: ['Non-Edibles(e.g. Packs)', 'Non-Edibles', 'Non Edibles'] // Added variations
};

/**
 * Custom hook for managing product data
 * Provides server-side filtering by category_id for accurate pagination and totals
 */
export const useProductData = (activeTab = 0) => {
  // ==========================================
  // Pagination State
  // ==========================================
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
  });

  // ==========================================
  // Search State
  // ==========================================
  const [search, setSearch] = useState("");

  // ==========================================
  // Pagination Handlers
  // ==========================================
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (per_page) => {
    setPagination({ page: 1, per_page });
  };

  // ==========================================
  // Search Handler
  // ==========================================
  const handleSearch = (searchTerm) => {
    const trimmedSearch = searchTerm.trim();
    setSearch(trimmedSearch);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // First, get all categories to find the correct IDs
  const { data: categoriesResponse, isLoading: categoriesLoading } = useGetCategoriesQuery();
  
  // Extract categories from response
  const categories = Array.isArray(categoriesResponse) 
    ? categoriesResponse 
    : categoriesResponse?.data || [];

  // 🔍 DEBUG: Log available categories
  console.log("🏷️ Available Categories in Database:", categories.map(c => ({ id: c.id, name: c.name })));

  // Helper function to find category ID by name(s)
  const findCategoryId = (categoryNames) => {
    const names = Array.isArray(categoryNames) ? categoryNames : [categoryNames];
    
    // First try: Exact match (case-insensitive)
    let category = categories.find(cat => 
      names.some(name => cat.name.toLowerCase() === name.toLowerCase())
    );
    
    // Second try: Partial match for longer names
    if (!category) {
      category = categories.find(cat => 
        names.some(name => {
          const catName = cat.name.toLowerCase();
          const searchName = name.toLowerCase();
          return catName.includes(searchName) && searchName.length > 3;
        })
      );
    }
    
    // 🔍 DEBUG: Log search results
    if (!category) {
      console.warn(`⚠️ Category not found for names:`, names);
    } else {
      console.log(`✅ Found category:`, { searchNames: names, found: { id: category.id, name: category.name } });
    }
    
    return category?.id;
  };

  // Get category IDs dynamically
  const foodCategoryId = findCategoryId(CATEGORY_NAMES.FOOD);
  const platterCategoryId = findCategoryId(CATEGORY_NAMES.PLATTER);
  const nonEdiblesCategoryId = findCategoryId(CATEGORY_NAMES.NON_EDITABLES);

  // 🔍 DEBUG: Log found category IDs
  console.log("🔍 Category IDs Found:", {
    food: foodCategoryId,
    platter: platterCategoryId,
    nonEdibles: nonEdiblesCategoryId,
  });

  // Determine which category to filter by based on active tab
  const getCategoryIdForTab = () => {
    switch (activeTab) {
      case 1: return platterCategoryId;      // Platter tab
      case 2: return nonEdiblesCategoryId;   // Non-edibles tab
      default: return null;                  // Products tab - show all (use null instead of '')
    }
  };

  const categoryIdForCurrentTab = getCategoryIdForTab();

  // 🔍 DEBUG: Log current tab's category filter
  console.log("🎯 Current Tab Filter:", {
    activeTab,
    tabName: activeTab === 0 ? 'Products' : activeTab === 1 ? 'Platter' : 'Non-Edibles',
    categoryId: categoryIdForCurrentTab,
    willFilter: categoryIdForCurrentTab !== null,
  });

  // Check if we're trying to filter by a category that doesn't exist
  const categoryMissing = (activeTab === 1 && !platterCategoryId) || 
                          (activeTab === 2 && !nonEdiblesCategoryId);

  // Make single API call with server-side category filtering
  // Skip API call if category is missing to avoid fetching all products
  const productsQuery = useGetProductsQuery({
    page: pagination.page,
    per_page: pagination.per_page,
    search: search,
    category_id: categoryIdForCurrentTab || '', // Use empty string instead of null for API
  }, {
    skip: categoryMissing, // Skip the query if category doesn't exist
  });

  // Extract data from the response structure
  const productsData = productsQuery.data || { data: [], total: 0, current_page: 1, per_page: 10, last_page: 1 };
  const productsList = productsData.data || [];

  // If category is missing, return empty results (no error)
  if (categoryMissing) {
    console.warn("⚠️ Category is missing for current tab, showing empty results");
    return {
      productsData: [],
      foodProducts: [],
      platterProducts: [],
      nonEdiblesProducts: [],
      totalProducts: 0,
      totalFood: 0,
      totalPlatter: 0,
      totalNonEdibles: 0,
      lowStockProducts: 0,
      outOfStockProducts: 0,
      isLoading: categoriesLoading,
      isFetching: false,
      error: null, // No error - just empty data
      isError: false, // Not an error state
      categoryIds: {
        food: foodCategoryId,
        platter: platterCategoryId,
        nonEdibles: nonEdiblesCategoryId
      },
      paginationInfo: {
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
      },
      handlePageChange,
      handlePageSizeChange,
      search,
      handleSearch,
      refetch: () => productsQuery.refetch()
    };
  }

  // Calculate low stock products
  const lowStockProducts = productsList.filter(product => 
    product.current_qty <= product.minimum_qty
  );

  // Calculate out of stock products
  const outOfStockProducts = productsList.filter(product => 
    product.current_qty === 0
  );

  // 🔍 DEBUG: Log query results
  console.log("📊 Query Results:", {
    activeTab,
    productsReturned: productsList.length,
    totalFromAPI: productsData.total,
  });

  return {
    // Raw data
    productsData: productsList,
    
    // Categorized data (same as raw for single call approach)
    foodProducts: activeTab === 0 ? productsList : [],
    platterProducts: activeTab === 1 ? productsList : [],
    nonEdiblesProducts: activeTab === 2 ? productsList : [],
    
    // Statistics
    totalProducts: productsData.total,
    totalFood: activeTab === 0 ? productsData.total : 0,
    totalPlatter: activeTab === 1 ? productsData.total : 0,
    totalNonEdibles: activeTab === 2 ? productsData.total : 0,
    lowStockProducts: lowStockProducts.length,
    outOfStockProducts: outOfStockProducts.length,
    
    // Loading states
    isLoading: categoriesLoading || productsQuery.isLoading,
    isFetching: productsQuery.isFetching,
    
    // Error states
    error: productsQuery.error,
    isError: !!productsQuery.error,
    
    // Category IDs (for automatic category selection in modals)
    categoryIds: {
      food: foodCategoryId,
      platter: platterCategoryId,
      nonEdibles: nonEdiblesCategoryId
    },
    
    // Pagination info
    paginationInfo: {
      current_page: productsData.current_page || 1,
      last_page: productsData.last_page || 1,
      per_page: productsData.per_page || 10,
      total: productsData.total || 0,
    },
    
    // Pagination handlers
    handlePageChange,
    handlePageSizeChange,
    
    // Search state and handler
    search,
    handleSearch,
     
    // Actions
    refetch: () => productsQuery.refetch()
  };
};